import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import LoadingComponent from '../../../components/common/LoadingComponent';
import {
  getLabs as getLabsService,
  getLabTestsCart,
  selectLabPartner,
  selectPatient,
  selectAddress as selectAddressService,
  getRecommendedPackageDetails,
  addAHCRequestToNSA,
  findingLabPartner,
} from '../../../services';
import { logEvent } from '../../../utils/logEvent';
import Lottie from 'react-lottie';
import searchLottie from '../../../lottie/searching_location_NSA.json';
import Pills from '../../lab-test/v2/DesignSystem/Pills';
import LabCard from './LabCard';
import RecommendedPackageModal from './RecommendedPackageModal';
import Navbar from '../../lab-test/v2/DesignSystem/Navbar';
import BottomSheet from '../../lab-test/v2/DesignSystem/BottomSheet';
import PatientList from '../../lab-test/v2/components/PatientList';
import TimeSelection from '../../lab-test/v2/components/TimeSelection';
import SavedAddressess from '../../lab-test/v2/components/SavedAddresses';
import AddNewAddress from '../../lab-test/v2/components/AddNewAddress';
import useStorage from '../../lab-test/v2/hooks/useStorage';
import styles from './LabSelector.module.scss';
import EmptyStateIcon from '../../../images/common/emptystate.png';
import LocationUnavailable from '../../../gif/Location Pin.gif';
import FindingLabPartnerGif from '../../../gif/finding-lab-partner.gif';
import { ReactComponent as CrossIcon } from '../../lab-test/v2/Icons/Cross.svg';
import useSessionStorage from '../../lab-test/v2/hooks/useStorage';
import PartialCashlessBanner from '../../lab-test/v2/components/PartialCashlessBanner';
import { GradientCarousel } from '@app/ui';

/* ---------------- Styled ---------------- */

const OuterContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow-y: hidden;
  background-color: #f6f6f8;
`;

const BodyContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

/* ---------------- Helpers ---------------- */

const FindingLabPartner = ({ data, onTryAnotherLocation }) => {
  return (
    <div className="flex flex-col h-full">
      <div className={`flex flex-col flex-1 ${styles.flpScrollable}`}>
        <div className={styles.flpIllustration}>
          <img src={FindingLabPartnerGif} alt="" height={148} width={197} />
        </div>

        <p className={styles.flpHeading}>{data?.pageData?.heading}</p>
        <p className={styles.flpSubHeading}>{data?.pageData?.subHeading}</p>

        <div className={styles.flpCard}>
          <div className={styles.flpCardTitle}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="17"
              viewBox="0 0 15 17"
              fill="none"
            >
              <path
                d="M15 9.42285C14.9999 13.4235 11.6421 16.667 7.5 16.667C3.35793 16.667 0.000103005 13.4235 0 9.42285V0H15V9.42285ZM6.5 3.33594V6.33594H3.5V8.33594H6.5V11.3359H8.5V8.33594H11.5V6.33594H8.5V3.33594H6.5Z"
                fill="#CFC8FF"
              />
            </svg>
            Package Details
          </div>

          <p className={styles.flpLabel}>Package-</p>
          <div className={styles.flpInnerCard}>
            <p className={styles.flpPackageName}>
              {data?.packageDetails?.packageName}
            </p>
            <p className={styles.flpTestCount}>
              Includes {data?.packageDetails?.testCount} Tests
            </p>
          </div>

          <p className={styles.flpLabel}>Address-</p>
          <div className={styles.flpInnerCardLast}>
            <p className={styles.flpAddress}>{data?.packageDetails?.address}</p>
          </div>
        </div>

        {data?.pageData?.disclaimer && (
          <p className={styles.flpDisclaimer}>{data?.pageData?.disclaimer}</p>
        )}
      </div>

      <button onClick={onTryAnotherLocation} className={styles.ctaButton}>
        {data?.pageData?.buttonCTA}
      </button>
    </div>
  );
};

const NoLabPartnerFound = ({ nsaObject, onCTAClick }) => {
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: searchLottie,
    rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col flex-1 justify-center items-center">
        <Lottie options={defaultOptions} height={200} width={200} />
        <p className={`text-primary mt-10px ${styles.nsaHeading}`}>
          {nsaObject?.heading}
        </p>
        <p className={`text-14 text-center ${styles.nsaSubHeading}`}>
          {nsaObject?.subHeading}
        </p>
      </div>
      <button onClick={onCTAClick} className={styles.ctaButton}>
        {nsaObject?.buttonCTA}
      </button>
    </div>
  );
};

const RenderEmptyContainer = ({ onClick }) => (
  <div className="flex flex-col justify-center items-center text-center p-24px">
    <img src={LocationUnavailable} alt="" />
    <p className="text-20 font-bold mt-12px">Enter your location</p>
    <p className="text-20 font-bold">to find partners near you</p>
    <button
      className="bg-primary text-white rounded-full py-4 px-8 mt-16"
      onClick={onClick}
    >
      Enter your location
    </button>
  </div>
);

/* ---------------- Main ---------------- */

const LabSelector = ({ history, location }) => {
  const authToken = useSelector((state) => state.user.authToken);
  const { getFromStorage } = useStorage();
  const queryParams = new URLSearchParams(
    location?.search || window.location.search
  );
  // `cartId` comes from the upsell redeem response (labsCartId); `cId` is the
  // older query key.
  const cartIdFromQuery = queryParams.get('cartId') || queryParams.get('cId');
  const [state, setState] = useState({
    loading: true,
    partnerLoading: true,
    labList: [],
    selectedLab: null,

    cartId: location.state?.cartId || cartIdFromQuery || '',

    currentLat: -1,
    currentLong: -1,
    currentLocation: '',
    pincode: '',
    tooltip: false,
    hasLocationPermission: true,

    reimburseAvailable: false,
    disclaimer: {},
    nsaObject: null,

    filter: 'all',

    showIncludedTest: false,
    testData: null,
    unavailableTestData: [],
    testCountLabel: '',
    showPriceBreakdown: false,
    priceData: null,

    showPackageModal: false,
    packageModalData: null,
    packageModalLab: null,
    packageSelectLoading: false,
    alternatePackageId: null,

    showPatientList: false,
    showSlotsModal: false,

    showSavedAddresses: false,
    showMapModal: false,
    showAddNewAddressModal: false,
    newAddress: null,
    editingAddressData: null,
    selectedPartnerId: null,
    selectedPartner: null,
    allLabList: [],
    labList: [],
    filter: 'all',
    showAddNewAddress: false,
    findingLabPartnerData: null,
    pendingNSAAction: false,
    visitClinicSection: null,
    visitClinicInfo: { heading: null, subHeading: null },
    partialCashlessBanner: null,
    bookingType: null,
  });

  const previousAddressIdRef = useRef(null);
  const hasLoggedScreenOpenedRef = useRef(false);

  const { setToStorage } = useSessionStorage();

  const updateState = (u) => setState((s) => ({ ...s, ...u }));

  const logAddressBottomSheetOpened = () => {
    const currentAddress = getFromStorage('selected_address');
    previousAddressIdRef.current = currentAddress?.addressId;
    logEvent('Address bottom sheet opened', {
      changeInAddress: currentAddress ? 'no' : 'no', // Initial open is not a change
      addressType: currentAddress?.addressId
        ? 'Saved Address'
        : 'Current location',
      addressId: currentAddress?.addressId,
      pincode: currentAddress?.pincode,
      city: currentAddress?.city,
      state: currentAddress?.state,
    });
  };

  const {
    partnerLoading,
    labList,
    cartId,
    currentLat,
    currentLong,
    currentLocation,
    tooltip,
    disclaimer,
    nsaObject,
    filter,
    showPatientList,
    showSlotsModal,
    showSavedAddresses,
    showMapModal,
    showAddNewAddressModal,
    selectedPartner,
    hasLocationPermission,
    visitClinicSection,
    visitClinicInfo,
  } = state;

  console.log('visitClinicSection', visitClinicSection);

  /* ---------------- Mount ---------------- */

  useEffect(() => {
    const nsaRequestId = location.state?.requestId;
    if (!nsaRequestId) return;

    findingLabPartner(authToken, nsaRequestId).then((data) => {
      updateState({
        findingLabPartnerData: data,
        hasLocationPermission: true,
        partnerLoading: false,
      });
    });
  }, []);

  useEffect(() => {
    if (location.state?.requestId) return;
    setTimeout(() => {
      const savedAddress = getFromStorage('selected_address');
      if (savedAddress?.lat && savedAddress?.long) {
        updateState({
          currentLat: savedAddress.lat,
          currentLong: savedAddress.long,
          currentLocation:
            savedAddress.formattedAddress || savedAddress.address,
          pincode: savedAddress.pincode,
          hasLocationPermission: true,
        });
      } else {
        updateState({ hasLocationPermission: false });
      }
    }, 500);
  }, [hasLocationPermission]);

  // Navbar / other components dispatch `location_selected` after writing
  // `selected_address` to storage (e.g. when user grants browser geolocation
  // prompt). Pick it up so labs refetch.
  useEffect(() => {
    if (location.state?.requestId) return;
    const handleLocationSelected = () => {
      const savedAddress = getFromStorage('selected_address');
      if (!savedAddress?.lat || !savedAddress?.long) return;
      // Saved address picked from the sheet: onAddressSelect updates the
      // location only after select-address resolves, so labs aren't fetched
      // against the old cart address.
      if (savedAddress.addressId) return;
      updateState({
        currentLat: savedAddress.lat,
        currentLong: savedAddress.long,
        currentLocation: savedAddress.formattedAddress || savedAddress.address,
        pincode: savedAddress.pincode,
        hasLocationPermission: true,
      });
    };
    window.addEventListener('location_selected', handleLocationSelected);
    return () => {
      window.removeEventListener('location_selected', handleLocationSelected);
    };
  }, []);

  useEffect(() => {
    if (state?.showIncludedTest) {
      logEvent('Test list viewed');
    }
    if (state?.showPriceBreakdown) {
      logEvent('View Transaction Details', {
        copayAmount: +(state.priceData?.postDiscountPrice || 0),
        partnerId: state.selectedPartnerId,
      });
    }
    if (state?.showSlotsModal) {
      logEvent('Slot Selection Screen Opened', {
        cartId: state.cartId,
        visitType: selectedPartner?.isHomeCollection
          ? 'Home Visit'
          : 'Clinic Visit',
      });
    }
  }, [state]);

  /* ---------------- Data ---------------- */

  const getLabs = useCallback(
    (lat, long, cId) => {
      updateState({ partnerLoading: true, labList: [] });

      // 🚀 ALWAYS call API with "all"
      const filterParam = 'fastestReport&nearestCentre';

      console.log('cId', cId);

      getLabsService(cId, lat, long, authToken, filterParam)
        .then((res) => {
          // Visit-clinic labs are surfaced only in the carousel section, so
          // keep them out of the main partner list to avoid showing twice.
          const list = (res?.networkList || []).filter((c) => !c.isVisitClinic);

          if (!hasLoggedScreenOpenedRef.current) {
            logEvent('Lab Partner Listing Screen Opened', {
              cartId,
              countOfPartners: list?.length,
            });
            hasLoggedScreenOpenedRef.current = true;
          }

          updateState({
            allLabList: list, // ✅ store original
            labList: list, // default = all
            reimburseAvailable: res.reimburse,
            disclaimer: res.disclaimer,
            nsaObject: res?.nsaObj,
            visitClinicSection: res?.visitClinicNetworkList
              ? res?.visitClinicNetworkList?.list
              : null,
            visitClinicInfo: {
              heading: res?.visitClinicNetworkList
                ? res?.visitClinicNetworkList?.heading
                : null,
              subHeading: res?.visitClinicNetworkList
                ? res?.visitClinicNetworkList?.subHeading
                : null,
            },
            partialCashlessBanner: res?.partialCashlessBanner || null,
            bookingType: res?.bookingType || null,
          });
        })
        .finally(() => {
          updateState({ partnerLoading: false });
        });
    },
    [authToken]
  );

  useEffect(() => {
    const { allLabList, filter } = state;

    if (!allLabList?.length) return;

    let sortedList = [...allLabList];

    if (filter === 'nearestCentre') {
      sortedList.sort(
        (a, b) =>
          (a.nearestCentreRank ?? Infinity) - (b.nearestCentreRank ?? Infinity)
      );
    }

    if (filter === 'fastestReport') {
      sortedList.sort(
        (a, b) =>
          (a.fastestReportRank ?? Infinity) - (b.fastestReportRank ?? Infinity)
      );
    }

    // filter === 'all' → original order
    updateState({ labList: sortedList });
  }, [state.filter, state.allLabList]);

  const getCart = useCallback(() => {
    getLabTestsCart(authToken, cartId)
      .then((res) => {
        updateState({ cartId: res.cartId });
      })
      .finally(() => updateState({ loading: false }));
  }, [authToken, cartId]);

  useEffect(() => {
    if (state.findingLabPartnerData) return;
    if (currentLat !== -1 && currentLong !== -1 && cartId) {
      getLabs(currentLat, currentLong, cartId);
      // make active tab to all
      updateState({ filter: 'all', hasLocationPermission: true });
    } else if (currentLat !== -1 && currentLong !== -1 && !cartId) {
      getCart();
    }
  }, [currentLat, currentLong, cartId, getLabs, getCart]);

  /* ---------------- Actions ---------------- */

  const handleBookLab = (ele) => {
    logEvent('Labs Partner Selected', {
      cartId,
      partnerId: ele.id,
      partnerName: ele.name,
      visitType: ele.isHomeCollection ? 'Home Visit' : 'Clinic Visit',
      visitClinicAvailable: state.visitClinicSection ? 'Yes' : 'No',
    });
    updateState({ selectedPartner: ele });

    const isHomeVisit = ele.isHomeCollection;
    const currentAddress = getFromStorage('selected_address');

    // Only home visits need a saved address for sample collection.
    // Center/clinic visits: skip the save-address prompt, go straight to slots.
    if (isHomeVisit && !currentAddress?.addressId) {
      logAddressBottomSheetOpened();
      updateState({ showAddNewAddressModal: true, pendingNSAAction: false });
      return;
    }

    if (!isHomeVisit && !currentAddress?.addressId) {
      selectLabPartner(cartId, ele.id, ele.meta, authToken).then(() => {
        updateState({ selectedLab: ele, showSlotsModal: true });
      });
      return;
    }

    selectAddressService(currentAddress?.addressId, cartId, authToken).then(
      () => {
        selectLabPartner(cartId, ele.id, ele.meta, authToken).then(() => {
          updateState({ selectedLab: ele, showSlotsModal: true });
        });
      }
    );
  };

  const renderLabCard = (ele, idx) => (
    <LabCard
      key={`${ele.id}-${idx}`}
      ele={ele}
      onBook={handleBookLab}
      onViewTests={(data) =>
        updateState({
          showIncludedTest: true,
          testData: data.cartTestItemList || [],
          unavailableTestData: data.unavailableItemList || [],
          testCountLabel: data.testCountLabel || '',
        })
      }
      onViewBreakdown={(data) => {
        updateState({
          showPriceBreakdown: true,
          priceData: data,
          selectedPartnerId: data?.id,
        });
      }}
      onPackageSelect={(pkg, lab) => {
        if (state.packageSelectLoading) return;
        logEvent('Package Recommendation Viewed');
        updateState({ packageSelectLoading: true });
        getRecommendedPackageDetails(authToken, {
          cartId,
          packageId: pkg.packageId,
          partnerId: lab.id,
        })
          .then((res) => {
            const pkgData = res?.recommendedPackages?.[0];
            if (pkgData) {
              updateState({
                showPackageModal: true,
                packageModalData: pkgData,
                packageModalLab: lab,
              });
            }
          })
          .finally(() => updateState({ packageSelectLoading: false }));
      }}
    />
  );

  /* ---------------- Render ---------------- */

  return (
    <OuterContainer>
      <Navbar
        onClick={() => {
          logAddressBottomSheetOpened();
          updateState({ showSavedAddresses: true });
        }}
        label="Select Lab Partner"
        iconColor="var(--primary-button, #714fff)"
        locationIconStyle={{
          color: 'var(--primary-button, #714fff)',
        }}
        showLocation
        hideBackButton={true}
      />

      {nsaObject === null && !state.findingLabPartnerData && (
        <div className={styles.pillsContainer}>
          <Pills
            showTitle={false}
            options={[
              { label: 'All', value: 'all' },
              { label: 'Express Report', value: 'fastestReport' },
              { label: 'Nearest', value: 'nearestCentre' },
            ]}
            selected={filter}
            onSelect={(v) => {
              if (partnerLoading) {
                return;
              }
              updateState({ filter: v });
            }}
          />
        </div>
      )}
      {state.partialCashlessBanner && (
        <PartialCashlessBanner data={state.partialCashlessBanner} />
      )}

      {!hasLocationPermission && (
        <RenderEmptyContainer
          onClick={() => {
            logAddressBottomSheetOpened();
            updateState({ showSavedAddresses: true });
          }}
        />
      )}

      {hasLocationPermission && partnerLoading && <LoadingComponent />}

      {state.packageSelectLoading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingSpinner} />
        </div>
      )}

      {hasLocationPermission && !partnerLoading && (
        <BodyContainer
          style={state.findingLabPartnerData ? { backgroundColor: '#fff' } : {}}
        >
          {visitClinicSection && (
            <GradientCarousel
              heading={visitClinicInfo?.heading}
              subHeading={visitClinicInfo?.subHeading}
              items={visitClinicSection}
              renderItem={renderLabCard}
              getItemKey={(lab, idx) => `${lab.id}-${idx}`}
            />
          )}

          <div className="mx-12px">{labList.map(renderLabCard)}</div>

          {labList?.length === 0 &&
            visitClinicSection?.length === 0 &&
            !nsaObject &&
            !state.findingLabPartnerData && (
              <div className="center col">
                <img src={EmptyStateIcon} alt="" />
                No partner available at selected location.
              </div>
            )}

          {nsaObject &&
            labList?.length === 0 &&
            !state.findingLabPartnerData && (
              <NoLabPartnerFound
                nsaObject={nsaObject}
                onCTAClick={() => {
                  const selectedAddress = getFromStorage('selected_address');
                  if (!selectedAddress?.addressId) {
                    updateState({
                      showAddNewAddressModal: true,
                      pendingNSAAction: true,
                    });
                    return;
                  }

                  addAHCRequestToNSA(
                    authToken,
                    selectedAddress.addressId,
                    cartId,
                    nsaObject?.packageId
                  ).then((res) => {
                    findingLabPartner(
                      authToken,
                      res?.requestId || res?.data?.requestId
                    ).then((data) => {
                      updateState({ findingLabPartnerData: data });
                    });
                  });
                }}
              />
            )}

          {state.findingLabPartnerData && (
            <FindingLabPartner
              data={state.findingLabPartnerData}
              onTryAnotherLocation={() =>
                updateState({
                  findingLabPartnerData: null,
                  showSavedAddresses: true,
                })
              }
            />
          )}
        </BodyContainer>
      )}

      {/* Patient */}
      <PatientList
        isOpen={showPatientList}
        serviceLine="labs"
        cartId={cartId}
        onClose={() => updateState({ showPatientList: false })}
        onPatientSelect={(patient) => {
          selectPatient(cartId, patient?.patientId).then(() => {
            updateState({ showPatientList: false, showSlotsModal: true });
          });
        }}
      />

      {/* Slots */}
      {showSlotsModal && (
        <TimeSelection
          cartId={cartId}
          isOpen={showSlotsModal}
          selectedPartner={selectedPartner}
          type={selectedPartner?.centerId ? 'Clinic Visit' : 'Home Visit'}
          onClose={() => updateState({ showSlotsModal: false })}
          onConfirm={(res) => {
            updateState({ showSlotsModal: false });
            const pushState = {
              cartId: res.cartId || cartId,
              isConciergeBooking: location?.state?.isConciergeBooking,
              bookingType: state.bookingType,
            };
            if (state.alternatePackageId) {
              pushState.isAlternatePackageSelected = true;
              pushState.alternatePackageId = state.alternatePackageId;
            }
            history.push('/lab-test/order-summary', pushState);
          }}
        />
      )}

      {/* Address */}
      <SavedAddressess
        isOpen={showSavedAddresses}
        onClose={() => updateState({ showSavedAddresses: false })}
        onAddressSelect={(address) => {
          logEvent('Address bottom sheet opened', {
            changeInAddress:
              address?.addressId !== previousAddressIdRef.current
                ? 'yes'
                : 'no',
            addressType: address?.addressId
              ? 'Saved Address'
              : 'Current location',
            addressId: address?.addressId,
            pincode: address.pincode,
            city: address.city,
            state: address.state,
          });

          // Show loader immediately to prevent old list flash
          updateState({
            partnerLoading: true,
            showSavedAddresses: false,
            nsaObject: null,
            findingLabPartnerData: null,
            labList: [],
            allLabList: [],
            filter: 'all',
          });

          const isSameLocation =
            address?.lat === currentLat && address?.long === currentLong;

          if (address?.addressId) {
            selectAddressService(address?.addressId, cartId, authToken).then(
              () => {
                if (isSameLocation) {
                  getLabs(currentLat, currentLong, cartId);
                } else {
                  updateState({
                    currentLat: address.lat,
                    currentLong: address.long,
                    currentLocation: address.address,
                  });
                }
              }
            );
          } else {
            if (isSameLocation) {
              getLabs(currentLat, currentLong, cartId);
            } else {
              updateState({
                currentLat: address?.lat,
                currentLong: address?.long,
                currentLocation: address?.address,
              });
            }
          }
        }}
      />

      {/* Tests Bottom Sheet */}
      <BottomSheet
        isOpen={state.showIncludedTest}
        noPadding
        onClose={() =>
          updateState({
            showIncludedTest: false,
            testData: null,
            unavailableTestData: [],
            testCountLabel: '',
          })
        }
      >
        <div>
          {/* Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 16,
            }}
          >
            <p
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#100E15',
              }}
            >
              {state.testCountLabel ||
                `${state.testData?.length || 0} Tests Included`}
            </p>

            <button
              onClick={() =>
                updateState({
                  showIncludedTest: false,
                  testData: null,
                  unavailableTestData: [],
                  testCountLabel: '',
                })
              }
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                height: 20,
                width: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                backgroundColor: '#f2edff',
              }}
            >
              <CrossIcon height={8} width={8} />
            </button>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid #E5E7EB' }} />

          {/* Scrollable Content */}
          <div
            style={{
              maxHeight: 450,
              overflow: 'scroll',
            }}
          >
            {state.testData?.map((test, idx) => (
              <div
                key={idx}
                style={{
                  fontSize: '14px',
                  color: '#100E15',
                  padding: 16,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom:
                    idx === state.testData?.length - 1 &&
                    !state.unavailableTestData?.length
                      ? 'none'
                      : '1px solid #E5E7EB',
                }}
              >
                <p style={{ margin: 0 }}>{test?.name || test?.testName}</p>

                {test?.price && (
                  <p style={{ margin: 0, fontWeight: '600' }}>₹{test.price}</p>
                )}
              </div>
            ))}
            {state.unavailableTestData?.map((test, idx) => (
              <div
                key={`unavailable-${idx}`}
                style={{
                  fontSize: '14px',
                  color: '#100E15',
                  padding: 16,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom:
                    idx === state.unavailableTestData?.length - 1
                      ? 'none'
                      : '1px solid #E5E7EB',
                  backgroundColor: '#FEF2F2',
                }}
              >
                <p style={{ margin: 0 }}>{test?.name || test?.testName}</p>
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#EF4444',
                    border: '1px solid #EF4444',
                    borderRadius: '12px',
                    padding: '2px 8px',
                    whiteSpace: 'nowrap',
                    backgroundColor: '#ffffff',
                  }}
                >
                  Unavailable
                </span>
              </div>
            ))}
          </div>
        </div>
      </BottomSheet>

      {/* Price Breakdown Bottom Sheet */}
      <BottomSheet
        isOpen={state.showPriceBreakdown}
        onClose={() =>
          updateState({ showPriceBreakdown: false, priceData: null })
        }
      >
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingBottom: 20,
            }}
          >
            <p
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#100E15',
              }}
            >
              Payment Details
            </p>
            <button
              onClick={() =>
                updateState({ showPriceBreakdown: false, priceData: null })
              }
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                height: 20,
                width: 20,
                backgroundColor: '#F2EDFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
              }}
            >
              <CrossIcon height={8} width={8} />
            </button>
          </div>
          <div
            style={{ borderTop: '1px solid #E5E7EB', marginBottom: 20 }}
          ></div>

          {/* Level 1 */}
          {/* Level 2 */}
          {/* Level 3 */}

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              marginTop: '10px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '14px',
                color: '#6B7280',
                marginBottom: 12,
              }}
            >
              <span>Total Cost</span>
              <span style={{ fontWeight: '600' }}>
                ₹{state.priceData?.preDiscountPrice || 0}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '14px',
                color: '#6B7280',
                marginBottom: 12,
              }}
            >
              <span>Discount</span>
              <span
                style={{
                  color: 'var(--primary-button, #714fff)',
                  fontWeight: '600',
                }}
              >
                -₹
                {state.priceData?.preDiscountPrice -
                  state.priceData?.postDiscountPrice || 0}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '16px',
                fontWeight: '600',
                color: '#100E15',
              }}
            >
              <span>You Pay</span>
              <span>₹{state.priceData?.postDiscountPrice || 0}</span>
            </div>
          </div>
        </div>
      </BottomSheet>

      {/* Recommended Package Modal */}
      {state.showPackageModal && state.packageModalData && (
        <RecommendedPackageModal
          pkg={state.packageModalData}
          onClose={() =>
            updateState({
              showPackageModal: false,
              packageModalData: null,
              packageModalLab: null,
            })
          }
          onBook={() => {
            const pkgId = state.packageModalData?.packageId;
            const pkgData = state.packageModalData;
            logEvent('Wipro Package book now clicked', {
              cartId,
              additionalTestNames: (pkgData?.additionalTests || []).map(
                (t) => t.name
              ),
              additionalTestIds: (pkgData?.additionalTests || []).map(
                (t) => t.id
              ),
              originalTestNames: (pkgData?.prescribedTests || []).map(
                (t) => t.name
              ),
              originalTestIds: (pkgData?.prescribedTests || []).map(
                (t) => t.id
              ),
              partnerName: state.packageModalLab?.name,
            });
            updateState({
              showPackageModal: false,
              packageModalData: null,
              packageModalLab: null,
              alternatePackageId: pkgId,
            });
            if (state.packageModalLab) handleBookLab(state.packageModalLab);
          }}
        />
      )}

      {/* Add new Address */}
      <AddNewAddress
        newAddress={getFromStorage('selected_address')}
        isOpen={showAddNewAddressModal}
        hideSkipButton={true}
        onSave={(res) => {
          const addressId = res?.address?.addressId;
          setToStorage('selected_address', res?.address);
          window?.dispatchEvent(new Event('location_selected'));
          updateState({
            showAddNewAddressModal: false,
            pendingNSAAction: false,
          });

          if (state.pendingNSAAction) {
            addAHCRequestToNSA(
              authToken,
              addressId,
              cartId,
              nsaObject?.packageId
            ).then((nsaRes) => {
              findingLabPartner(
                authToken,
                nsaRes?.requestId || nsaRes?.data?.nsaRequestId
              ).then((data) => {
                updateState({ findingLabPartnerData: data });
              });
            });
          } else {
            selectAddressService(addressId, cartId, authToken).then(() => {
              selectLabPartner(
                cartId,
                selectedPartner?.id,
                selectedPartner?.meta,
                authToken
              ).then(() => {
                updateState({ showSlotsModal: true });
              });
            });
          }
        }}
        onClose={() => updateState({ showAddNewAddressModal: false })}
      />
    </OuterContainer>
  );
};

export default LabSelector;
